import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ExternalLink, Phone, Mail } from 'lucide-react';

const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await axios.get(`${API}/vendors`);
        setVendors(res.data);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };
    fetchVendors();
  }, []);

  const categories = [...new Set(vendors.map((v) => v.category))];

  return (
    <div data-testid="vendors-page" className="bg-[#F5F5F0]">
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1507504031003-b417219a0fde?w=1920')`,
          }}
        />
        <div className="absolute inset-0 bg-[#4A5D23]/60" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
          <span className="font-accent text-4xl text-[#D4AF37] mb-4">Our Partners</span>
          <h1 className="font-heading text-5xl md:text-7xl font-light">Preferred Vendors</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <p className="text-[#6B7280] max-w-3xl mx-auto text-lg leading-relaxed">
            We work with the finest vendors throughout The Bahamas to ensure every aspect of your event is executed 
            with excellence. These trusted partners share our commitment to quality and attention to detail.
          </p>
        </div>
      </section>

      {/* Vendors by Category */}
      <section className="section-padding bg-[#F5F5F0]">
        <div className="container-custom">
          {categories.map((category) => (
            <div key={category} className="mb-16 last:mb-0">
              <h2 className="font-heading text-3xl text-[#2C2C2C] mb-8 text-center">
                {category}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vendors
                  .filter((v) => v.category === category)
                  .map((vendor, index) => (
                    <div
                      key={vendor.id}
                      data-testid={`vendor-card-${index}`}
                      className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-hover transition-all duration-300"
                    >
                      <div className="flex items-start gap-4 mb-4">
                        {vendor.logo_url ? (
                          <img
                            src={vendor.logo_url}
                            alt={vendor.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-[#E8DFD0] flex items-center justify-center">
                            <span className="font-heading text-2xl text-[#D4AF37]">
                              {vendor.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div>
                          <h3 className="font-heading text-xl text-[#2C2C2C]">{vendor.name}</h3>
                          <span className="text-sm text-[#D4AF37]">{vendor.category}</span>
                        </div>
                      </div>
                      
                      {vendor.description && (
                        <p className="text-[#6B7280] text-sm mb-4">{vendor.description}</p>
                      )}
                      
                      <div className="space-y-2 text-sm">
                        {vendor.phone && (
                          <a 
                            href={`tel:${vendor.phone}`}
                            className="flex items-center gap-2 text-[#6B7280] hover:text-[#D4AF37] transition-colors"
                          >
                            <Phone size={14} />
                            {vendor.phone}
                          </a>
                        )}
                        {vendor.email && (
                          <a 
                            href={`mailto:${vendor.email}`}
                            className="flex items-center gap-2 text-[#6B7280] hover:text-[#D4AF37] transition-colors"
                          >
                            <Mail size={14} />
                            {vendor.email}
                          </a>
                        )}
                        {vendor.website && (
                          <a 
                            href={vendor.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-[#4A5D23] hover:text-[#D4AF37] transition-colors"
                          >
                            <ExternalLink size={14} />
                            Visit Website
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <span className="font-accent text-3xl text-[#D4AF37]">Join Our Network</span>
          <h2 className="font-heading text-4xl md:text-5xl text-[#2C2C2C] mt-2 mb-6">
            Become a Preferred Vendor
          </h2>
          <p className="text-[#6B7280] max-w-2xl mx-auto mb-8">
            Are you a vendor who shares our commitment to excellence? We'd love to hear from you.
          </p>
          <Link to="/contact">
            <Button
              data-testid="vendor-cta-btn"
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full px-8"
            >
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default VendorsPage;
